import type { Reservation, ReservationPaymentDetails } from '@appTypes/api.types';
import type { ApiErrorResponse } from '@core/interceptors/api.interceptor';

/**
 * ADLC-4 agency payment rules, the website side (QA doc: docs features/agency-payment-rules/README.md, rules R-W1 .. R-W8; contract
 * `.adlc/spec/ADLC-4-reservation-details.openapi.yaml`). The members below are the ADDITIVE part of `ReservationPaymentDetails`
 * (online-api passes them through verbatim); `src/types/api.types.ts` is generated and does not carry them yet, so they live here.
 * Display only: every figure is the API's, nothing is computed from another figure (D1).
 */

/** `none` = no schedule (the page works as before), `unavailable` = the payment-plans service did not answer (C17). Absent = none. */
export type PaymentScheduleState = 'available' | 'none' | 'unavailable';

export type NextPaymentKind = 'next' | 'overdue' | 'paid' | 'closed';

export type PaymentStepStatus = 'due' | 'partly' | 'paid' | 'overdue' | 'closed';

/** The code core-service answers when the payment-plans service cannot be reached (the one code of the contract). */
export const PAYMENT_PLANS_UNAVAILABLE = 'PAYMENT_PLANS_UNAVAILABLE';

/**
 * R-W7 / R-W8 (C3 / C17): the readable message of each code the payment boxes show, an English key with its ru-RU line (C21).
 * Amiran 29.10.2026: when the payment-plans service is down the booking list still works - only the Overdued filter and the next
 * payment show this coded message with a copyable Trace ID. A code not listed here falls back to the PAYMENT_PLANS_UNAVAILABLE text.
 */
export const PAYMENT_PLAN_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  [PAYMENT_PLANS_UNAVAILABLE]: 'Payment schedules are not available right now'
};

/** The translation key of a payment box's coded message. */
export function paymentPlanErrorMessage(code: string | null | undefined): string {
  const known = code ? textOrNull(PAYMENT_PLAN_ERROR_MESSAGES[code]) : null;
  return known ?? PAYMENT_PLAN_ERROR_MESSAGES[PAYMENT_PLANS_UNAVAILABLE];
}

/** A text the sender may leave empty: an empty string counts as missing, like null. */
export function textOrNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  return value;
}

const isSet = (value: unknown): boolean => value !== null && value !== undefined;

export interface NextPayment {
  kind: NextPaymentKind;
  /** next: due date of the earliest not fully paid step; overdue: due date of the EARLIEST overdue step (the "since" date). */
  dueDate?: string | null;
  /** The remaining amount of that step. */
  amount?: number | null;
  percentOfTotal?: number | null;
  currencyCode?: string | null;
  stepNo?: number | null;
  overdueDays?: number | null;
}

export interface PaymentScheduleStep {
  stepNo: number;
  dueDate: string;
  percent: number;
  amount: number;
  paid: number;
  /** Negative on the last step when overpaid. */
  outstanding: number;
  status: PaymentStepStatus;
  overdueDays?: number | null;
  isNext?: boolean;
}

export interface PaymentSchedule {
  currencyCode: string;
  total: number;
  closed?: boolean;
  steps: PaymentScheduleStep[];
}

export interface PaymentScheduleError {
  errorCode?: string | null;
  traceId?: string | null;
}

/** `ReservationPaymentDetails` with the ADLC-4 members. */
export interface ReservationPaymentPlanDetails extends ReservationPaymentDetails {
  paymentScheduleState?: PaymentScheduleState | null;
  paymentScheduleError?: PaymentScheduleError | null;
  nextPayment?: NextPayment | null;
  paymentSchedule?: PaymentSchedule | null;
}

/** What one reservation shows: the state, the coded error (unavailable only), the next payment line and the step list. */
export interface PaymentPlanView {
  state: PaymentScheduleState;
  errorCode: string | null;
  traceId: string | null;
  next: NextPayment | null;
  schedule: PaymentSchedule | null;
  currencyCode: string | null;
}

const KNOWN_STATES: readonly PaymentScheduleState[] = ['available', 'none', 'unavailable'];

const carriesPlan = (details: ReservationPaymentPlanDetails | null | undefined): boolean =>
  !!details && (isSet(details.paymentScheduleState) || isSet(details.nextPayment) || isSet(details.paymentSchedule));

/** The block that carries the plan: the invoice-currency block first, the selling-currency block otherwise. */
function planBlock(reservation: Reservation | null | undefined): ReservationPaymentPlanDetails | undefined {
  const invoiceBlock: ReservationPaymentPlanDetails | undefined = reservation?.paymentInOutInvCurrency;
  if (carriesPlan(invoiceBlock)) return invoiceBlock;
  const sellBlock: ReservationPaymentPlanDetails | undefined = reservation?.paymentInSellCurrency;
  return carriesPlan(sellBlock) ? sellBlock : undefined;
}

/**
 * Reads the payment plan of one reservation. The plan is kept in the currency of the agency invoices, so the invoice-currency block
 * (`paymentInOutInvCurrency`) is read first and the selling-currency block only when the first one does not carry it.
 * An unknown or missing state is `none` (an older sender) - the page then works as it did before ADLC-4.
 */
export function readPaymentPlan(reservation: Reservation | null | undefined): PaymentPlanView {
  const details = planBlock(reservation);
  const rawState = details?.paymentScheduleState;
  const state: PaymentScheduleState = rawState && KNOWN_STATES.includes(rawState) ? rawState : 'none';

  if (state === 'unavailable') {
    return {
      state,
      errorCode: textOrNull(details?.paymentScheduleError?.errorCode) ?? PAYMENT_PLANS_UNAVAILABLE,
      traceId: textOrNull(details?.paymentScheduleError?.traceId),
      next: null,
      schedule: null,
      currencyCode: null
    };
  }

  const schedule = state === 'available' && details?.paymentSchedule?.steps?.length ? details.paymentSchedule : null;
  if (!schedule)
    return { state: 'none', errorCode: null, traceId: null, next: null, schedule: null, currencyCode: null };

  return {
    state,
    errorCode: null,
    traceId: null,
    next: details?.nextPayment ?? fallbackNext(schedule),
    schedule,
    currencyCode:
      textOrNull(details?.nextPayment?.currencyCode) ??
      textOrNull(schedule.currencyCode) ??
      textOrNull(details?.currencyCode)
  };
}

/** Only the two states the step list proves on its own; any other missing `nextPayment` shows no line (never a guessed figure). */
function fallbackNext(schedule: PaymentSchedule): NextPayment | null {
  if (schedule.closed) return { kind: 'closed' };
  if (schedule.steps.every((step) => step.status === 'paid')) return { kind: 'paid' };
  return null;
}

/**
 * R-W5: the Payment schedule tab of a reservation is shown when it has steps - always, also while other reservations of the
 * list cannot be read (KZ-BUG-260) - or while its own schedule is unavailable (the tab then says so with the coded box), but
 * not while the list is down: then the box above the list already says it and the unreadable card has no tab.
 */
export function hasPaymentScheduleTab(reservation: Reservation | null | undefined, listDown: boolean): boolean {
  const { state } = readPaymentPlan(reservation);
  if (state === 'available') return true;
  return state === 'unavailable' && !listDown;
}

/** `ok` = nothing to say; `list-down` = payments of two or more reservations did not answer; `overdued-down` = the Overdued search was refused. */
export type PaymentListStateKind = 'ok' | 'list-down' | 'overdued-down';

export interface PaymentListState {
  kind: PaymentListStateKind;
  /** Test id prefix of the box: `reservations-overdued` when the agency asked for Overdued, else `reservations-payment`. */
  prefix: 'reservations-overdued' | 'reservations-payment';
  errorCode: string | null;
  traceId: string | null;
}

/**
 * The state of the result list (R-W7 / R-W8, AC-104):
 *   - the search itself refused with PAYMENT_PLANS_UNAVAILABLE (the Overdued filter needs the service) -> `overdued-down`, no cards;
 *   - payments of TWO OR MORE reservations say `unavailable` -> `list-down`, one box above the cards, each of those cards
 *     "Next payment: not available"; the cards that did load keep their band and tab (R-W7);
 *   - ONE reservation says `unavailable` (alone, or among others that loaded) -> `ok`; its own card shows the coded box with
 *     Try again (R-W6) - one unreadable reservation never turns the whole list down (KZ-BUG-260).
 */
export function paymentListState(
  reservations: readonly Reservation[] | null | undefined,
  searchError: ApiErrorResponse | null | undefined,
  overduedFilter: boolean
): PaymentListState {
  if (searchError?.errorCode === PAYMENT_PLANS_UNAVAILABLE) {
    return {
      kind: 'overdued-down',
      prefix: 'reservations-overdued',
      errorCode: PAYMENT_PLANS_UNAVAILABLE,
      traceId: textOrNull(searchError.activityId)
    };
  }
  const down = (reservations ?? []).map(readPaymentPlan).filter((plan) => plan.state === 'unavailable');
  if (down.length > 1) {
    return {
      kind: 'list-down',
      prefix: overduedFilter ? 'reservations-overdued' : 'reservations-payment',
      errorCode: down[0].errorCode,
      traceId: down[0].traceId
    };
  }
  return { kind: 'ok', prefix: 'reservations-payment', errorCode: null, traceId: null };
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** `1 234.50 EUR` - two decimals, a space between thousands, a real minus sign for a negative (overpaid) figure. */
export function formatPlanMoney(value: number | null | undefined, currencyCode?: string | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const rounded = round2(value);
  const [whole, cents] = Math.abs(rounded).toFixed(2).split('.');
  const sign = rounded < 0 ? '−' : '';
  return sign + groupThousands(whole) + '.' + cents + (currencyCode ? ' ' + currencyCode : '');
}

/** `1234567` -> `1 234 567` (a plain loop: no backtracking regular expression). */
function groupThousands(digits: string): string {
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ' ';
    out += digits[i];
  }
  return out;
}

/** `30 %` / `12.5 %` - one decimal, a whole number without one. */
export function formatPlanPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const rounded = Math.round(value * 10) / 10;
  return (Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)) + ' %';
}

/** `dd.mm.yyyy` (C24) from the API's `yyyy-mm-dd` (or an ISO date-time); the text as it came when it is not a date. */
export function formatPlanDate(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}

/** The translation key of a step status (code-style keys, en-US.json + ru-RU.json). Overdue counts its days. */
export const PAYMENT_STEP_STATUS_KEYS: Record<PaymentStepStatus, string> = {
  due: 'PAYMENT_STEP_DUE',
  partly: 'PAYMENT_STEP_PARTLY',
  paid: 'PAYMENT_STEP_PAID',
  overdue: 'PAYMENT_STEP_OVERDUE_DAYS',
  closed: 'PAYMENT_STEP_CLOSED'
};

/** Colour of a step status (the theme's tokens); overdue also carries "! " so colour is never the only signal (WCAG 1.4.1). */
export const PAYMENT_STEP_STATUS_CLASSES: Record<PaymentStepStatus, string> = {
  due: '',
  partly: 'text-yellow-500',
  paid: 'text-kz-success',
  overdue: 'text-kz-danger',
  closed: 'text-kz-grey-400'
};
